import ms from 'ms'
import { CSSProperties } from 'react'
import { Props as ReactSelectProps } from 'react-select'

import { ReferralsTab } from '../pages/referrals/constants'
import { OrderDirection } from '../types/api'
import { ImportDirection } from '../types/dtos/bulk-import-referral-details'
import {
  CommercialAgreementReferralDirection,
  CommercialAgreementReviewStatus,
  CommercialAgreementStatus,
  CommercialAgreementType,
  CompanyEmailType,
  CompanyIdentifierType,
  CompanyRegister,
  CompanyStatus,
  CompanyTypeCode,
  CompassPdfReportSection,
  CompassReportStatus,
  ConsentDecisionType,
  ConsentType,
  DoughnutChartSize,
  FcaType,
  FeeSharingType,
  IntellifloConfigSection,
  IntellifloStatusType,
  LockedClientDetailsReason,
  MeetingLocationType,
  NormalReferralTypeOption,
  PanelRequestApprovalStatus,
  PanelStatus,
  PendingPanelAction,
  PlannrConfigSection,
  PlannrStatusType,
  RecommendedPartnerActionType,
  ReferralApprovalStatus,
  ReferralDirectionCommercialAgreementStatus,
  ReferralLinkClientFieldType,
  ReferralLinkScheduler,
  ReferralStatusCode,
  ReferralTarget,
  ReferralType,
  ReferralTypeOption,
  RqCertifiedAssessmentStage,
  RqCertifiedAssessmentStatus,
  SelectOption,
  StateKey,
  StateType,
  VirtualMeetingPlatformType,
} from '../types/misc'
import { ROUTES } from './routes'
import { FEE_CATEGORY_OPTION_OTHER } from './services/serviceFeeService'

export const REFERRAL_DIRECTION_COMMERCIAL_AGREEMENT_STATUS: Record<
  ReferralDirectionCommercialAgreementStatus,
  ReferralDirectionCommercialAgreementStatus
> = {
  CURRENT_AGREEMENT_AWAITING_REVIEW_BY_US:
    'CURRENT_AGREEMENT_AWAITING_REVIEW_BY_US',
  CURRENT_AGREEMENT_AWAITING_REVIEW_BY_THEM:
    'CURRENT_AGREEMENT_AWAITING_REVIEW_BY_THEM',
  PENDING_AGREEMENT_AWAITING_REVIEW_BY_US:
    'PENDING_AGREEMENT_AWAITING_REVIEW_BY_US',
  PENDING_AGREEMENT_AWAITING_REVIEW_BY_THEM:
    'PENDING_AGREEMENT_AWAITING_REVIEW_BY_THEM',
  COMPLETE: 'COMPLETE',
}

export const COMMERCIAL_AGREEMENT_STATUS: Record<
  CommercialAgreementStatus,
  CommercialAgreementStatus
> = {
  CURRENT: 'CURRENT',
  PENDING: 'PENDING',
}

export const COMMERCIAL_AGREEMENT_REVIEW_STATUS: Record<
  CommercialAgreementReviewStatus,
  CommercialAgreementReviewStatus
> = {
  AWAITING_REVIEW_BY_US: 'AWAITING_REVIEW_BY_US',
  AWAITING_REVIEW_BY_THEM: 'AWAITING_REVIEW_BY_THEM',
  COMPLETE: 'COMPLETE',
}

export const FEE_SHARING_TYPE = {
  percentage: 'PERCENTAGE',
  setAmount: 'SET_AMOUNT',
} satisfies Record<string, FeeSharingType>

export const SERVICE_FEE_CATEGORY_TYPES = {
  initialFees: {
    label: 'Initial fees',
    value: 'INITIAL_FEE',
  },
  ongoingFees: {
    label: 'Ongoing fees',
    value: 'ONGOING_FEE',
  },
  arrangementFee: {
    label: 'Arrangement fee',
    value: 'ARRANGEMENT_FEE',
  },
  totalFee: {
    label: 'Total fees',
    value: 'TOTAL_FEE',
  },
  other: {
    label: 'Other - please specify',
    value: FEE_CATEGORY_OPTION_OTHER,
  },
}

export const SERVICE_FEE_SHARE_TYPE = {
  fixedFee: {
    label: 'Fixed Fee',
    value: FEE_SHARING_TYPE.setAmount,
  },
  percentage: {
    label: 'Percentage',
    value: FEE_SHARING_TYPE.percentage,
  },
}

export const SERVICE_FEE_DURATION = {
  lifeOfTheContract: '-1',
}

export const SERVICE_FEE_DURATION_OPTIONS: SelectOption[] = [
  {
    label: 'The life of the contract',
    value: SERVICE_FEE_DURATION.lifeOfTheContract,
  },
  { label: '1 year', value: '1' },
  { label: '2 years', value: '2' },
  { label: '3 years', value: '3' },
  { label: '4 years', value: '4' },
  { label: '5 years', value: '5' },
  { label: '6 years', value: '6' },
  { label: '7 years', value: '7' },
  { label: '10 years', value: '10' },
]

export const SIDEBAR_POSITION = {
  LEFT: 'left',
  RIGHT: 'right',
}

export const SIDEBAR_BEHAVIOR = {
  STICKY: 'sticky',
  FIXED: 'fixed',
  COMPACT: 'compact',
}

export const LAYOUT = {
  FLUID: 'fluid',
  BOXED: 'boxed',
}

export const THEME = {
  DEFAULT: 'default',
  COLORED: 'colored',
  DARK: 'dark',
  LIGHT: 'light',
}

/** @todo these should be CSS vars. E.g. var(--bs-primary) */
export const THEME_PALETTE_LIGHT = {
  bodyBackground: '#f7f9fc',
  bodyColor: '#212529',
  borderColor: '#dee6ed', // --bs-border-color
  base: '#6c757d',
  blue: '#3B82EC',
  blueLight: '#E2F1FF',
  compassPdfTextColor: '#5D5D5D',
  primary: '#3B82EC',
  'primary-dark': '#1659c7',
  'primary-light': '#84aef2',
  secondary: '#495057',
  green: '#4BBF73',
  success: '#4BBF73',
  successDark: '#99D7AE',
  successLight: '#DDF2E4',
  successExtraLight: '#DDF2E4',
  info: '#F3FAFF',
  orange: '#f0ad4e',
  warning: '#f0ad4e',
  warningLight: '#FFD198',
  warningExtraLight: '#FFFBF6',
  red: '#d9534f',
  redExtraLight: '#FFE0DC',
  redLight: '#FFA295',
  tableStripedBg: '#f4f7f9',
  danger: '#d9534f',
  dangerLight: '#FF5353',
  dangerExtraLight: '#d88c8c',
  purple: '#7F4EBED9',
  disabled: '#e2e8ee',
  white: '#fff',
  gray: '#F7F8FA',
  '#D5D5D5': '#D5D5D5',
  grayBox: '#F8F8F8',
  grayLight: '#D1D4D7',
  'gray-100': '#f4f7f9',
  'gray-200': '#e2e8ee',
  'gray-300': '#dee6ed',
  'gray-400': '#ced4da',
  'gray-500': '#adb5bd',
  'gray-600': '#6c757d',
  'gray-700': '#495057',
  'gray-800': '#020202',
  'gray-900': '#212529',
  statLabelColor: '#b6b6b6',
  black: '#000',
  yellowDocuSign: '#ffc820',
  fontWeights: {
    bold: 500,
  },
  integration: {
    INTELLIFLO: '#0F1ECD',
  },
}

export const CHART_PALETTE = {
  blue: '#46a1f8',
  yellow: '#ffa600',
  blueDark: '#2f79c0',
  orange: '#EC7A08',
  red: '#F8333C',
  pink: '#fb8489',
  pinkDark: '#bc5090',
  purple: '#58508d',
  green: '#89be28',
  greyLight: 'rgb(215,215,215)',
  yellowDark: '#e6d000',
  greenDark: '#6b941f',
  greyDark: 'rgba(107, 107, 107, 1)',
}

// Can merge this into the main palette if we need to use the colors outside
// the PDF report
export const COMPASS_PDF_REPORT_PALETTE = {
  blue: '#06B0F0',
}

// TODO: We're only using the light theme, so merge THEME_PALETTE_DARK and
// THEME_PALETTE_LIGHT into one THEME_PALETTE object
export const THEME_PALETTE_DARK = {
  ...THEME_PALETTE_LIGHT,
  'primary-dark': '#84aef2',
  'primary-light': '#1659c7',
  white: '#293042',
  'gray-100': '#3e4555',
  'gray-200': '#545968',
  'gray-300': '#696e7b',
  'gray-400': '#7f838e',
  'gray-500': '#9498a1',
  'gray-600': '#a9acb3',
  'gray-700': '#bfc1c6',
  'gray-800': '#d4d6d9',
  'gray-900': '#eaeaec',
  black: 'black',
  dangerAlternate: '#FF644F',
}

export const COMPASS_REPORT_STATUS = {
  INVITED: 'INVITED',
  COMPLETE: 'COMPLETE',
  IN_PROGRESS: 'IN_PROGRESS',
} satisfies Record<CompassReportStatus, CompassReportStatus>

export const CONSENT_TYPE: Record<ConsentType, ConsentType> = {
  ICAEW: 'ICAEW',
}
export const CONSENT_DECISION: Record<
  ConsentDecisionType,
  ConsentDecisionType
> = {
  OPT_IN: 'OPT_IN',
  OPT_OUT: 'OPT_OUT',
}

export const COMPANY_IDENTIFIER: Record<
  CompanyIdentifierType,
  CompanyIdentifierType
> = {
  frn: 'frn',
  sraNumber: 'sraNumber',
  companyId: 'companyId',
}

export const COMPANY_STATUS: Record<CompanyStatus, CompanyStatus> = {
  SUSPENDED: 'SUSPENDED',
  CLAIMED: 'CLAIMED',
}

export const COMPANY_TYPE_CODE: Record<CompanyTypeCode, CompanyTypeCode> = {
  ifa: 'ifa',
  solicitor: 'solicitor',
  accountant: 'accountant',
  mortgage_adviser: 'mortgage_adviser',
  business_lending_non_fca: 'business_lending_non_fca',
  insurance_adviser: 'insurance_adviser',
  corporate_finance: 'corporate_finance',
  corporate_finance_non_fca: 'corporate_finance_non_fca',
  investment_management: 'investment_management',
  auto_enrolment: 'auto_enrolment',
  business_coach: 'business_coach',
  conveyancing: 'conveyancing',
  employee_benefits_provider: 'employee_benefits_provider',
  estates_admin: 'estates_admin',
  estates_agent: 'estates_agent',
  estate_planner: 'estate_planner',
  management_consultant: 'management_consultant',
  marketing_consultant: 'marketing_consultant',
  property_developer: 'property_developer',
  software_provider: 'software_provider',
  surveyor: 'surveyor',
  venture_capital: 'venture_capital',
  other: 'other',
}

export const COMPANY_TYPE_EMAIL: Record<CompanyEmailType, CompanyEmailType> = {
  COMPASS_INVITE: 'COMPASS_INVITE',
}

export const COMMERCIAL_AGREEMENT_TYPE: Record<
  CommercialAgreementType,
  CommercialAgreementType
> = {
  NO: 'NO',
  YFS: 'YFS',
  YJV: 'YJV',
  RQP: 'RQP',
}

export const COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION: Record<
  CommercialAgreementReferralDirection,
  CommercialAgreementReferralDirection
> = {
  INCOMING: 'INCOMING',
  OUTGOING: 'OUTGOING',
}

export const EMAIL_ADDRESSES = {
  rqHello: 'hello@rq.app',
  rqSupport: 'support@rq.app',
}

export const RQ_CERTIFIED_ASSESSMENT_STATUS_TYPE = {
  notStarted: 'NOT_STARTED',
  pendingPaymentConfirmation: 'PENDING_PAYMENT_CONFIRMATION',
  inProgress: 'IN_PROGRESS',
  inReview: 'IN_REVIEW',
  passed: 'PASSED',
  failed: 'FAILED',
} satisfies Record<string, RqCertifiedAssessmentStatus>

export const RQ_CERTIFIED_ASSESSMENT_STAGE = {
  one: 'one',
  two: 'two',
} satisfies Record<RqCertifiedAssessmentStage, RqCertifiedAssessmentStage>

/**
 * @see https://date-fns.org/docs/format for date formats.
 */
export const DATE_FORMATS = {
  DAY_MONTH_YEAR: 'dd/LL/yyyy', // 05/03/2002
  DAY_MONTH_YEAR_TIME: 'dd/LL/yyyy, h:mm a', // 05/03/2002, 2:53 PM
  MONTH_YEAR: 'LL/yyyy', // 05/2002
  TIME: 'h:mmaaa', // '2:53pm'
  YEAR_MONTH_DAY: 'yyyy-LL-dd', // 2002-05-03
  YEAR_MONTH_DAY_TIME: 'yyyy-LL-dd H:mm', // 2002-05-03 14:53
  MONTH_DAY_YEAR: 'MMM d, yyyy', // May 3, 2002
  MONTH_DAY_YEAR_TIME: 'MMM d, yyyy h:mm a', // May 3, 2002 2:53 PM
  LONG_DAY_MONTH_YEAR: 'do MMMM yyyy', // 3rd November 2002
  LONG_DAY_MONTH_YEAR_TIME: 'do MMMM yyyy h:mm a', // 3rd November 2002, 2:52 PM
}

export const ERROR_MESSAGES = {
  genericServerError:
    'Sorry but there was a problem on our side. ' +
    'Our team has been notified and will attempt to fix the issue as soon ' +
    'as possible. Please try again later.',
  required: 'This field is required',
  blacklistedEmail:
    'That looks like a personal email account. Please enter a business email.',
}

export const FCA_MONITORING_CODE = {
  CompaniesHouse: 'CompaniesHouse',
  FCA: 'FCA',
  FOS: 'FOS',
  FSCS1: 'FSCS1',
  FSCS2: 'FSCS2',
}

export const ORDER_DIRECTION: Record<OrderDirection, OrderDirection> = {
  asc: 'asc',
  desc: 'desc',
}

export const FCA_SERVICE_TYPE_NAMES = {
  independent: 'Independent',
  restrictedNotWholeOfMarket: 'Restricted - Not whole of market',
  restrictedWholeOfMarket: 'Restricted - Whole of market',
  no: 'No',
}

export const FCA_SERVICE_TYPE_RESTRICTION_LEVELS = {
  independent: 1,
  restrictedNotWholeOfMarket: 2,
  restrictedWholeOfMarket: 3,
  no: 4,
}

export const LOCAL_STORAGE_KEYS = {
  apiCommitHash: 'apiCommitHash',
  isProgrammaticReload: 'isProgrammaticReload',
  previouslySelectedScope: 'previouslySelectedScope',
  previouslySelectedCompanyId: 'previouslySelectedCompanyId', // Only read this when refreshing the page to pre-select company
}

export const PANEL_STATUS: Record<PanelStatus, PanelStatus> = {
  requested: 'requested',
  approved: 'approved',
  rejected: 'rejected',
}

export const PENDING_PANEL_ACTION: Record<
  PendingPanelAction,
  PendingPanelAction
> = {
  addCommercialAgreement: 'addCommercialAgreement',
  reviewCurrentAgreement: 'reviewCurrentAgreement',
  chaseCurrentAgreementReview: 'chaseCurrentAgreementReview',
  reviewPendingAgreement: 'reviewPendingAgreement',
  chasePendingAgreementReview: 'chasePendingAgreementReview',
  respondToInvite: 'respondToInvite',
  reviewCurrentIncomingAgreementConsentLetter:
    'reviewCurrentIncomingAgreementConsentLetter',
  reviewPendingIncomingAgreementConsentLetter:
    'reviewPendingIncomingAgreementConsentLetter',
}

export const COOKIES = {
  referringCompanyReferralCode: 'rq.referring_company_referral_code',
}

const DUMMY_HUBSPOT_MEETING_LINK =
  'https://meetings-eu1.hubspot.com/james-valentine'

const IS_DEV = process.env.NODE_ENV === 'development'

export const EXTERNAL_LINKS = {
  dpbLicence:
    'https://www.icaew.com/regulation/dpb-investment-business-licence',
  marketingWebsite: 'https://www.rq.app',
  hubspot: {
    bookDemoLink: IS_DEV
      ? DUMMY_HUBSPOT_MEETING_LINK
      : 'https://meetings-eu1.hubspot.com/johnny-ridd',
    bookSlotWithRqLink: IS_DEV
      ? DUMMY_HUBSPOT_MEETING_LINK
      : 'https://meetings-eu1.hubspot.com/johnny-ridd/rq-demo',
  },
  privacyPolicy: 'https://www.rq.app/privacy-policy',
  rqRoadmap: 'https://app.loopedin.io/rq',
  rqCertified: 'https://www.rq.app/rq-certified',
  vimeo: {
    inviteToRq: 'https://player.vimeo.com/video/818424533?h=806c1e01ef',
    makeReferral: 'https://player.vimeo.com/video/818431321?h=e4ba16bd74',
    manageReferrals: 'https://player.vimeo.com/video/818422648?h=9ed2453ad9',
    manageRelationships:
      'https://player.vimeo.com/video/818423647?h=762c07dac0',
    compass: 'https://player.vimeo.com/video/818428419?h=5dec9b0238',
  },
}

export const FCA_ACCREDITATIONS = [
  { code: 'CISI', name: 'CISI Accredited' },
  { code: 'CII', name: 'CII Chartered' },
  { code: 'BCorp', name: 'BCorp' },
  { code: 'CEFEX', name: 'CEFEX' },
]

export const FCA_INDIVIDUAL_QUALIFICATIONS = [
  { code: 'Resolution', name: 'Resolution' },
  { code: 'SOLLA', name: 'SOLLA' },
  { code: 'STEP', name: 'STEP' },
  { code: 'STEP TEP', name: 'STEP TEP' },
  { code: 'STEP Full Member', name: 'STEP full member' },
]

export const FCA_SERVICE_TYPE_IDS = {
  independent: 1,
  restrictedNotWholeOfMarket: 2,
  restrictedWholeOfMarket: 3,
  no: 4,
}

export const RECOMMENDED_PARTNER_ACTION_TYPE: Record<
  RecommendedPartnerActionType,
  RecommendedPartnerActionType
> = {
  DISMISS: 'DISMISS',
  ACCEPT_WITH_TERMS: 'ACCEPT_WITH_TERMS',
  ACCEPT_WITHOUT_TERMS: 'ACCEPT_WITHOUT_TERMS',
}

export const REFERRAL_TYPE: Record<ReferralType, ReferralType> = {
  direct: 'direct',
  indirect: 'indirect',
}

export const REFERRAL_TYPE_OPTION: Record<
  ReferralTypeOption,
  ReferralTypeOption
> = {
  direct: 'direct',
  directCc: 'directCc',
  directPco: 'directPco',
  indirect: 'indirect',
  bookCallForClient: 'bookCallForClient',
}

export const REFERRAL_APPROVAL_STATUS: Record<
  ReferralApprovalStatus,
  ReferralApprovalStatus
> = {
  approved: 'approved',
  pending: 'pending',
  rejected: 'rejected',
}

export const NORMAL_REFERRAL_TYPE_OPTION: Record<
  NormalReferralTypeOption,
  NormalReferralTypeOption
> = {
  direct: 'direct',
  directCc: 'directCc',
  directPco: 'directPco',
  indirect: 'indirect',
}

export const REFERRAL_STATUS_ID = {
  PENDING: 1,
  AWAITING_CLIENT_TO_SCHEDULE_MEETING: 2,
  AWAITING_ADVISER_TO_CONTACT_CLIENT: 3,
  AWAITING_CLIENT_CONSENT: 50,
  INITIAL_MEETING_SCHEDULED: 450,
  COMPLETE: 500,
}

export const REFERRAL_STATUS_CODE: Record<
  ReferralStatusCode,
  ReferralStatusCode
> = {
  awaiting_client_consent: 'awaiting_client_consent',
  awaiting_client_meeting: 'awaiting_client_meeting',
  awaiting_client_schedule_meeting: 'awaiting_client_schedule_meeting',
  complete: 'complete',
  initial_meeting_held: 'initial_meeting_held',
  initial_meeting_scheduled: 'initial_meeting_scheduled',
  live: 'live',
  won_completed: 'won_completed',
  not_progressing: 'not_progressing',
  unable_contact_client: 'unable_contact_client',
  awaiting_client_response: 'awaiting_client_response',
  client_details_passed_to_pco: 'client_details_passed_to_pco',
  not_sure: 'not_sure',
}

export const SERVICE_AREA_ID = {
  creationOfAFinancialPlan: 6,
  generalEnquiry: 1000,
  will: 1070,
}

export const MAX_FILE_SIZE_BYTES = 20000000

// Max range for MySQL integer types is 2147483647
// https://tinyurl.com/mysql-integer-types
export const MAX_INTEGER_VALUE = 2000000000

// Assuming we're using the MySQL DECIMAL(10,2) type for money columns
export const MAX_MONEY_VALUE = 1000000000

export const STATE_KEY = {
  hasInteractedWithFirstRelationshipRequestApprovedModal:
    'interacted_with_first_relationship_request_approved_modal',
  hasWatchedDemoVideo: 'watched_demo_video',
  hasInteractedWithRqCertifiedAnnouncement:
    'interacted_with_rq_certified_announcement',
  hasInteractedWithRqRoadmapBanner: 'interacted_with_rq_roadmap_banner',
  hasInteractedWithWalkthroughBanner: 'interacted_with_walkthrough_banner',
  hasInteractedWithIntellifloIntegrationAutosaveNotice:
    'interacted_with_intelliflo_integration_autosave_notice',
  hasInteractedWithIcaewPricingModal: 'interacted_with_icaew_pricing_modal',
  hasInteractedWithPlannrIntegrationAutosaveNotice:
    'interacted_with_plannr_integration_autosave_notice',
  hasClientReviewedPartners: 'client_reviewed_partners',
  panelApprovedNew: 'panel_approved_new',
  panelApprovedUpdated: 'panel_approved_updated',
  panelRequestedNew: 'panel_requested_new',
  panelRequestedUpdated: 'panel_requested_updated',
  panelRequestPending: 'panel_request_pending',
  referralFromNew: 'referral_from_new',
  referralFromUpdated: 'referral_from_updated',
  referralToNew: 'referral_to_new',
  referralToUpdated: 'referral_to_updated',
  referralApprovalRequestNew: 'referral_approval_request_new',
} satisfies Record<string, StateKey>

export const THEME_PALETTE = THEME_PALETTE_LIGHT

export const STATE_TYPE: Record<StateType, StateType> = {
  user: 'user',
  company: 'company',
  companyUser: 'companyUser',
  group: 'group',
  groupUser: 'groupUser',
}

export const COMMON_DATA_ERROR_MESSAGE =
  'Common data endpoint returned empty results'

export const MONTH_OPTION_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export const FCA_TYPE: Record<FcaType, FcaType> = {
  appointed_representative: 'appointed_representative',
  directly_authorised: 'directly_authorised',
  trading_name: 'trading_name',
}

export const DOUGHNUT_CHART_SIZE: Record<DoughnutChartSize, number> = {
  sm: 100,
  md: 140,
}

export const COMPASS_REPORT_PAGE_NAMES = {
  cover: 'Cover',
  tableOfContents: 'Table of contents',
  introduction: 'Introduction',
}

export const PDF_DIMENSIONS = {
  A4: {
    landscapeWidth: '297mm',
    landscapeHeight: '210mm',
    portraitWidth: '210mm',
    portraitHeight: '297mm',
  },
}
export const IMPORT_FILE_NOTIFICATION_DURATION = ms('7s')
export const IMPORT_REFERRALS_FILE_NOTIFICATION_DURATION = ms('2s')

export const PDF_PATHS = [
  ROUTES.compassPdfReportSection,
  ROUTES.compassPdfGuide,
  ROUTES.referralESignatureConsentPdf,
  ROUTES.rqCertifiedPdfGuides,
  ROUTES.rqCertifiedNdaPdf,
]

export const COMPASS_REPORT_SECTION = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
} satisfies Record<string, CompassPdfReportSection>

export const COMPASS_PALETTE = {
  orange: '#F18F01',
  turquoise: '#81D2C7',
  blue: '#06B0F0',
}

export const BASE_FONT_SIZE = 13.2

export const LONG_MESSAGE_DURATION = 7000

export const REGULATOR_CODE = {
  icaew: 'ICAEW',
  acca: 'ACCA',
  icas: 'ICAS',
  sra: 'SRA',
  ls: 'LS',
  other: 'OTHER',
}

export const REGULATOR_ID = {
  other: 100,
}

export const API_VIOLATION_CODE = {
  attachmentSize: 'app-attachment-size',
  invalidEnumValue: 'invalid-enum-value',
  missingCommercialTerms: 'app-commercial-agreement-no-service-fee',
  missingReferralLinkIndividuals: 'app-missing-referral-link-individuals',
  unapprovedOrUnamendedConsentLetter:
    'app-commercial-agreement-unreviewed-consent-letter',
  referralNonStandaloneService: 'app-referral-non-standalone-service',
  referralLinkNonStandaloneService: 'app-referral-link-non-standalone-service',
  ukPostcodeInvalid: 'uk-postcode-invalid',
  relationshipExists: 'app-relationship-exists',
  relationshipRejected: 'app-relationship-rejected',
}

export const RQ_DETAILS = {
  companyNumber: '13202073',
  address: '71-75 Shelton Street, Covent Garden, London, WC2H 9JQ',
}

export const LOCATION_STATE = {
  skipNavigationPrompt: 'skipNavigationPrompt',
}

export const EXISTING_AGREEMENT_MAX_FILE_SIZE_MB = 20

export const POSTHOG_EVENT = {
  compassBulkInviteError: 'Compass bulk invite error',
  login: 'Login',
  logout: 'Logout',
  errorMessageShownToUser: 'Error message shown to user',
  sessionConflictShownToUser: 'Session conflict shown to user',
  referralLinkNotWorking: 'Calendar link not working',
  switchGroup: 'Switch Group',
  switchCompany: 'Switch Company',
  switchClient: 'Switch Client',
}

// TODO: Add the other app areas here
export const POSTHOG_APP_AREA = {
  addCompany: 'add-company',
  completeRegistrationModal: 'complete-registration-modal',
}

export const DEFAULT_REACT_SELECT_PROPS: ReactSelectProps = {
  menuPlacement: 'auto',
  menuPortalTarget: document.body,
}

export const COMPASS_HEALTH_INDICATOR_OPTIONS = [
  '0-10',
  '10-20',
  '20-30',
  '30-40',
  '40-50',
  '50-60',
  '60-70',
  '70-80',
  '80-90',
  '90-100',
]

export const COMPANY_ADMIN_ROLE_IRI = '/v1/roles/2'
export const GROUP_ADMIN_ROLE_IRI = '/v1/roles/11'

export const ROLE_ID = {
  companyUserAdmin: 2,
  groupUserAdmin: 11,
}

export const GROUP_USER_PERMISSION_OPTIONS = [
  { label: 'Admin', value: [GROUP_ADMIN_ROLE_IRI] },
  { label: 'Member', value: [] },
]

export const COMPANY_USER_PERMISSION_OPTIONS = [
  { label: 'Admin', value: [COMPANY_ADMIN_ROLE_IRI] },
  { label: 'Member', value: [] },
]

// For search params that are reused across pages
export const SEARCH_PARAMS = {
  backLabel: 'backLabel',
  backUrl: 'backUrl',
  scrollY: 'scrollY',
}
export const COMPASS_NET_WORTH_PALETTE = [
  COMPASS_PALETTE.blue,
  COMPASS_PALETTE.turquoise,
  '#357DED',
  COMPASS_PALETTE.orange,
  ...Object.values(CHART_PALETTE),
]

export const NUM_COMPANY_REVIEWS_PER_PAGE = 3
export const NUM_COMPANY_INTEGRATION_LOGS_PER_PAGE = 5

export const COMMON_ACCOUNTANT_REGULATOR_CODES = [
  REGULATOR_CODE.icaew,
  REGULATOR_CODE.acca,
  REGULATOR_CODE.icas,
]

export const SRA_SOLICITOR_REGULATOR_CODES = [
  REGULATOR_CODE.ls,
  REGULATOR_CODE.sra,
]

export const NO_REGULATOR_VALUE = 'no-regulator'

export const OTHER_REGULATOR_VALUE = REGULATOR_ID.other.toString()

export const SHOW_MORE_REGULATORS_VALUE = 'show-more'

export const COMPANY_DASHBOARD_SIDEBAR_LINKS = {
  compass: 'Compass',
  referrals: 'Referrals',
  contacts: 'Contacts',
  referYourClient: 'Refer your client',
  yourRelationships: 'Professional network',
}

export const CLIENT_DASHBOARD_SIDEBAR_LINKS = {
  compassDashboard: 'Dashboard',
  yourSnapshots: 'Your Snapshots',
  actions: 'Actions',
  startCompass: 'Start Compass',
  continueCompass: 'Continue Compass',
  ourPartners: 'Our Partners',
  updateCompass: 'Update Compass',
  sharing: 'Sharing',
  howItWorks: 'How it works',
}

export const GROUP_DASHBOARD_SIDEBAR_LINKS = {
  referrals: 'Referrals',
  revenue: 'Revenue',
  stats: 'Stats',
  teamManagement: 'Team Management',
}

export const REFERRALS_DATA_STALE_TIME = ms('20s')

export const REFERRALS_TAB: Record<ReferralsTab, ReferralsTab> = {
  sent: 'sent',
  received: 'received',
}

export const REFERRALS_PAGE_SEARCH_PARAMS = { tab: 'tab' }

export const COMPANY_REGISTER: Record<string, CompanyRegister> = {
  companiesHouse: 'CompaniesHouse',
  fca: 'FCA',
  sra: 'SRA',
}

export const DEFAULT_BULK_IMPORT_CIRCUMSTANCES = 'bulk referral'
export const DEFAULT_BULK_IMPORT_REASON = 'bulk referral'

export const LOCKED_CLIENT_DETAILS_REASON = {
  awaitingClientConsent: 'AWAITING_CLIENT_CONSENT',
  awaitingClientCallback: 'AWAITING_CLIENT_CALLBACK',
  incompleteCommercialAgreement: 'INCOMPLETE_COMMERCIAL_AGREEMENT',
  unembeddableScheduleCallUrl: 'UNEMBEDDABLE_SCHEDULE_CALL_URL',
} satisfies Record<string, LockedClientDetailsReason>

export const DARK_PLACEHOLDER_STYLES: CSSProperties = {
  color: '#5D607F',
  opacity: '0.3',
}

export const INTELLIFLO_CONFIG_SECTION: Record<
  IntellifloConfigSection,
  IntellifloConfigSection
> = {
  adviserMapping: 'adviserMapping',
  statusMapping: 'statusMapping',
  defaultImportStatus: 'defaultImportStatus',
  feeStatusMapping: 'feeStatusMapping',
  documentMapping: 'documentMapping',
  import: 'import',
}

export const INTELLIFLO_STATUS_TYPE: Record<
  IntellifloStatusType,
  IntellifloStatusType
> = {
  lead: 'lead',
  client: 'client',
}

export const PLANNR_CONFIG_SECTION: Record<
  PlannrConfigSection,
  PlannrConfigSection
> = {
  adviserMapping: 'adviserMapping',
  statusMapping: 'statusMapping',
  defaultImportStatus: 'defaultImportStatus',
  feeStatusMapping: 'feeStatusMapping',
  documentMapping: 'documentMapping',
  import: 'import',
}

export const PLANNR_STATUS_TYPE: Record<PlannrStatusType, PlannrStatusType> = {
  client: 'client',
}

export const REFERRAL_LINK_SCHEDULER: Record<
  ReferralLinkScheduler,
  ReferralLinkScheduler
> = {
  NYLAS: 'NYLAS',
  CALENDLY: 'CALENDLY',
}

/** @see NylasRedirectEnum in the backend */
export type NylasRedirect = 0 | 1 | 2 | 3

/** @see NylasRedirectEnum in the backend */
export const NYLAS_REDIRECT: Record<
  | 'emailAndCalendarIntegration'
  | 'gettingStarted'
  | 'completeRegistrationFlow'
  | 'completeRegistrationFlowAtSignup',
  NylasRedirect
> = {
  emailAndCalendarIntegration: 0,
  gettingStarted: 1,
  completeRegistrationFlow: 2,
  completeRegistrationFlowAtSignup: 3,
}

export const TOAST_AUTOCLOSE_DELAY = 2500

export const UNREALIZED_COMPANY_ID = 0

export const PAYMENTS_PROCESSING_REFRESH_INTERVAL = 2500

export const PAYMENTS_RELOAD_REFRESH_INTERVAL = 8000

export const MODAL_IDS = {
  icaewConsentModal: 'icaewConsentModal',
}

export const PANEL_AGREEMENT_MESSAGE = {
  reviewAgreement: 'An agreement is pending your approval',
  chaseAgreementReview: "An agreement is pending your partner's approval",
}

export const DOM_IDS = {
  reviewConsentLetterBox: 'js-review-consent-letter-box',
  serviceFeeErrors: 'js-service-fee-errors',
}

export const MEETING_LOCATION_TYPE: Record<
  MeetingLocationType,
  MeetingLocationType
> = {
  PHYSICAL: 'PHYSICAL',
  VIRTUAL: 'VIRTUAL',
}

export const REFERRAL_LINK_CLIENT_FIELD_TYPE: Record<
  ReferralLinkClientFieldType,
  ReferralLinkClientFieldType
> = {
  CIRCUMSTANCES: 'CIRCUMSTANCES',
  DATE_OF_BIRTH: 'DATE_OF_BIRTH',
  ADDRESS: 'ADDRESS',
}

export const REFERRAL_LINK_CLIENT_FIELD_DEFAULT_VALUES: Record<
  ReferralLinkClientFieldType,
  boolean
> = {
  CIRCUMSTANCES: true,
  DATE_OF_BIRTH: false,
  ADDRESS: false,
}

export const VIRTUAL_MEETING_PLATFORM: Record<
  VirtualMeetingPlatformType,
  VirtualMeetingPlatformType
> = {
  GOOGLE_MEET: 'GOOGLE_MEET',
  MICROSOFT_TEAMS: 'MICROSOFT_TEAMS',
  ZOOM: 'ZOOM',
  OTHER: 'OTHER',
}

export const EXPECTED_COMMISSION_AMOUNT_UNSURE = -1

export const REFERRAL_STATUS_UPDATE_DISABLED_REASON = {
  NO_COMPANY: 'NO_COMPANY',
  STATUS_LOCKED: 'STATUS_LOCKED',
  COMMERCIAL_AGREEMENT_INCOMPLETE: 'COMMERCIAL_AGREEMENT_INCOMPLETE',
  INTELLIFLO_REFERRAL: 'INTELLIFLO_REFERRAL',
  NOT_TO_COMPANY: 'NOT_TO_COMPANY',
  OTHER: 'OTHER',
}

export const PANEL_REQUEST_APPROVAL_STATUS: Record<
  PanelRequestApprovalStatus,
  PanelRequestApprovalStatus
> = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
}

export const IDENTITY_PROVIDERS = {
  USERNAME_PASSWORD: 'Username + Password',
  MICROSOFT: 'Microsoft',
  GOOGLE: 'Google',
}

export const REFERRAL_IMPORT_DIRECTION: Record<
  ImportDirection,
  ImportDirection
> = {
  FROM: 'FROM',
  TO: 'TO',
}

// Used by <ReferralTargetSearch />
export const REFERRAL_TARGET: Record<ReferralTarget, ReferralTarget> = {
  individual: 'individual',
  serviceArea: 'serviceArea',
  company: 'company',
  companyType: 'companyType',
}
